import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import { getRemotedbUrl, i18nIntl, isNotEmpty } from '../../lib/ep.js';

import ActionButton, { SecondaryButton } from '../builder/ActionButton';
import ButtonCollection from '../builder/ButtonCollection.js';
import FloatingLayer from '../builder/FloatingLayer';
import Model from '../builder/Model';
import SectionTitle from '../builder/SectionTitle';
import SectionSubtitle from '../builder/SectionSubtitle.js';
import Spinner from '../builder/Spinner.js';
import Range from '../builder/Range.js';
import Select from '../builder/Select.js';
import Input from '../builder/Input.js';

import TriviaBulkCreate from './TriviaBulkCreate.js';
import Message, { AlertMessage } from '../builder/Message.js';

const max_answers = 5;

const fake_questions = JSON.parse('[["Question","Answer 1","Answer 2","Answer 3","Answer 4"],["\u00bfQu\u00e9 tipo de concursos se pueden crear con EasyPromos?","Sorteos","Votaciones","Quizzes","Concursos de fotos"],["\u00bfCu\u00e1l es la duraci\u00f3n m\u00e1xima de un concurso en EasyPromos?","1 semana","1 mes","3 meses","No hay l\u00edmite"],["\u00bfSe pueden personalizar los formularios de participaci\u00f3n en EasyPromos?","S\u00ed","No","Solo algunos elementos","Solo los colores"],["\u00bfEs posible integrar EasyPromos con otras plataformas?","S\u00ed","No","Solo con redes sociales","Solo con correos electr\u00f3nicos"],["\u00bfCu\u00e1l es el rango de precios de los planes de EasyPromos?","Gratuito","De $10 a $100","De $100 a $500","M\u00e1s de $500"],["\u00bfSe pueden a\u00f1adir restricciones de edad para participar en EasyPromos?","S\u00ed","No","Solo para menores de edad","Solo para mayores de edad"],["\u00bfSe pueden programar publicaciones autom\u00e1ticas en EasyPromos?","S\u00ed","No","Solo en redes sociales","Solo en el sitio web"],["\u00bfEs posible exportar los datos de participantes en EasyPromos?","S\u00ed","No","Solo en formato PDF","Solo en formato Excel"]]');

const AIGenerator = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const promotion = props?.promotion ?? {};
	const getLangName = (lang) => {
		if(window.locales == undefined) return "Not defined";

		const lang_name = window.locales[lang];
		if(lang_name != undefined) return lang_name;

		return "Not defined";
	};
	
	const available_langs_to_add = [{ k:promotion?.default_locale ?? 'eng', v:getLangName(promotion?.default_locale ?? 'eng') }];
	(window?.promoLocales ?? []).map((lang) => {
		available_langs_to_add.push({ k:lang.I18nPromotion.lang, v:getLangName(lang.I18nPromotion.lang) });
	});

	const promotion_id = promotion?.id ?? false;

	const default_ai = {
		text:'',
		num_questions:10,
		num_answers:3,
		language:available_langs_to_add[0].k,
		model:'gpt-3.5-turbo',
		questions:null,
		type:'timed_quiz',
		stage_id:props?.stage_id ?? null,
	};

	const [ ai, setAi ] = useState(default_ai);
	const [ searchingAI, setSearchingAI ] = useState(false);
	const [ step_2, setStep2 ] = useState(false);
	const [ api_error, setApiError ] = useState(false);
	const [ already, setAlready ] = useState([]);

	if(promotion_id === false) return null;

	const close = () => {
		if(props.close != undefined) props.close();
	};

	const available_to_search = (ai.text.length > 3) ? true : false;

	const change = (k, v) => {
		setAi({ ...ai, [k]:v });
	};

	const searchAI = (cb) => {
		setSearchingAI(true);

		let type = 'ai_timed_quiz';
		$.post({
			url:getRemotedbUrl(`/promotions/search_ai/${promotion_id}`),
			data:{ ...ai, ...{'questions':[]}},
			dataType:'JSON',
			success:(r) => {
				setSearchingAI(false);
				if(r.status == 1 && r.questions != undefined) {
					setAi({ ...ai, ...{'questions':r.questions}});
					if(r.already)setAlready(r.already);
					setStep2(true);
				} else {
					setApiError(true);
					//alert(`Api Error: ${r.error}`);
				}
				if(cb != undefined)cb();
			},
			error:(jqXHR, textStatus, errorThrown) => {
				close();
				setSearchingAI(false);

				let track_data = {};
				track_data.error = `JS ERROR: ${errorThrown}`;
				track_data.ai = ai;
				$.post(`/sweeps/track_error_log/${type}/${promotion_id}`, track_data,'JSON');
				setApiError(true);
			}
		});
	};

	const has_ai_questions = (isNotEmpty(ai, 'questions')) ? true : false;

	return (
		<FloatingLayer
			backButton={(!searchingAI) ? () => close() : null}
			_class={{ size:(!step_2) ? 'small' : 'huge' }}
			enable_close={(!searchingAI) ? true : false}
		>
			{(!step_2) && (
				<div>
					<SectionTitle>AI Question Generator</SectionTitle>

					<SectionSubtitle>Generate your own questions and answers through Artificial Intelligence.</SectionSubtitle>
					{(api_error) ? (
						<AlertMessage>
							<p>{i18n("Error connecting to the AI, please try again in a few minutes, and if the problem persists, contact support.")}</p>
						</AlertMessage>
					) : (
						<Model model={ai} change={change}>
							<Input
								name="text"
								max_length={50}
							>Topic of the questions</Input>

							<Select
								name="language"
								values={available_langs_to_add}
								_class={{ size:60 }}
							>Select a language</Select>

							<Range
								min={2}
								max={20}
								value={parseInt(ai?.num_questions ?? 1)}
								name="num_questions"
								default={1}
								step={1}
								_class={{ size:50 }}
							>Number of questions</Range>

							<Range
								min={2}
								max={max_answers}
								value={parseInt(ai?.num_answers ?? 2)}
								name="num_answers"
								default={2}
								step={1}
								_class={{ size:50 }}
							>Answers per question</Range>
						</Model>
					)}

					{(searchingAI) && (
						<Spinner
							autotranslate={false}
							force_bg={true}
						>{`${i18n("AI in process, please wait. This could take a while")}...`}</Spinner>
					)}

					<ButtonCollection in_floating={true}>

						{(!api_error) && (
							<ActionButton
								onClick={() => searchAI()}
								disabled={!available_to_search}
							>Generate questions</ActionButton>
						)}
						<SecondaryButton
							onClick={() => close()}
							autotranslate={false}
						>{i18n("Close")}</SecondaryButton>
					</ButtonCollection>
				</div>
			)}

			{(step_2) && (
				<div>
					<SectionTitle>AI Question Generator</SectionTitle>

					<TriviaBulkCreate
						max_answers={max_answers}
						stage_id={props.stage_id}
						promotion_id={promotion_id}
						transferQuestions={props.transferQuestions}
						text={(has_ai_questions) ? ai.questions : ''}
						already={already}
						from_ai={has_ai_questions}
						in_layer={true}
						max_rows_in_preview={20}
						default_sepator='tab'
						display_main_options={false}
						go_back={() => setStep2(false)}
						onCreate={() => close()}
						confirm_message="I have reviewed the generated questions and answers and I want to continue"
						confirm_label="Add to game"
						cancel_label="Go back"
						max_height='400px'
						extra_msg={<Message _class={{ other:'in_bottom' }}>
							<p>{i18n("You will be able to edit the question and answers after you add them to the game")}.</p>
							<p>{i18n("If there are many repeated questions, specify the topic more and generate new ones")}.</p>
						</Message>}
					/>
				</div>
			)}
		</FloatingLayer>
	);
}

export default injectIntl(AIGenerator);